// src/hooks/useStudentDocuments.js
import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// ---- Config ----
const BUCKET = 'student-docs';
const MAX_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
const ALLOWED_MIME = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  // add more as needed
]);

// Small helpers
const getExt = (filename = '') => {
  const i = filename.lastIndexOf('.');
  return i >= 0 ? filename.slice(i + 1).toLowerCase() : '';
};

const uuid = () =>
  crypto?.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

export const useStudentDocuments = () => {
  const [items, setItems] = useState([]);   // current list for a student
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---- List documents for a student ----
  const list = useCallback(async (studentId) => {
    if (!studentId) return { success: true, data: [] };
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('student_documents') // or 'rism.student_documents'
        .select('*')
        .eq('student_id', studentId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setItems(data || []);
      setError(null);
      return { success: true, data: data || [] };
    } catch (err) {
      console.error('list student_documents error:', err);
      setError(err.message);
      setItems([]);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ---- Upload a file and create metadata row ----
  const upload = useCallback(
    async (studentId, file, { docType = 'university_certificate', notes = '' } = {}) => {
      try {
        if (!studentId) throw new Error('studentId is required');
        if (!file) throw new Error('file is required');
        if (!docType) throw new Error('docType is required');

        // Basic validation
        if (!ALLOWED_MIME.has(file.type)) {
          throw new Error(`Unsupported file type: ${file.type || 'unknown'}. Allowed: PDF, PNG, JPEG.`);
        }
        if (file.size > MAX_SIZE_BYTES) {
          throw new Error(`File too large. Max ${Math.round(MAX_SIZE_BYTES / (1024 * 1024))} MB.`);
        }

        // Build a stable path
        const ext = getExt(file.name) || (file.type === 'application/pdf' ? 'pdf' : 'bin');
        const fileId = uuid();
        const path = `students/${studentId}/certificates/${fileId}.${ext}`;

        // 1) Upload to storage
        const { error: uploadErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { contentType: file.type, upsert: false });
        if (uploadErr) throw uploadErr;

        // 2) Insert metadata
        const { data: inserted, error: insertErr } = await supabase
          .from('student_documents') // or 'rism.student_documents'
          .insert([
            {
              student_id: studentId,
              doc_type: docType,
              storage_path: path,
              filename: file.name || `${fileId}.${ext}`,
              mime_type: file.type,
              size_bytes: file.size,
              notes,
              is_deleted: false,
            },
          ])
          .select()
          .single();

        if (insertErr) {
          // Best effort rollback: remove uploaded file if DB insert fails
          await supabase.storage.from(BUCKET).remove([path]);
          throw insertErr;
        }

        // Update local state
        setItems((prev) => [inserted, ...prev]);
        return { success: true, data: inserted };
      } catch (err) {
        console.error('upload student_document error:', err);
        setError(err.message);
        return { success: false, error: err.message };
      }
    },
    []
  );

  // ---- Signed URL for viewing/downloading ----
  const getSignedUrl = useCallback(async (storagePath, expiresInSec = 60) => {
    try {
      if (!storagePath) throw new Error('storagePath is required');

      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(storagePath, expiresInSec);

      if (error) throw error;
      return { success: true, url: data?.signedUrl || null };
    } catch (err) {
      console.error('getSignedUrl error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // ---- Remove (soft or hard) ----
  /**
   * soft delete: set is_deleted = true
   * hard delete: also remove the file from storage
   */
  const remove = useCallback(async (docId, { hardDelete = false } = {}) => {
    try {
      if (!docId) throw new Error('docId is required');

      // (a) find the row to know the storage_path
      const { data: row, error: readErr } = await supabase
        .from('student_documents') // or 'rism.student_documents'
        .select('id, storage_path')
        .eq('id', docId)
        .single();

      if (readErr) throw readErr;

      // (b) soft delete in DB
      const { error: updErr } = await supabase
        .from('student_documents')
        .update({ is_deleted: true })
        .eq('id', docId);

      if (updErr) throw updErr;

      // (c) hard delete in storage if requested
      if (hardDelete && row?.storage_path) {
        const { error: rmErr } = await supabase.storage.from(BUCKET).remove([row.storage_path]);
        if (rmErr) throw rmErr;
      }

      // local state
      setItems((prev) => prev.filter((x) => x.id !== docId));
      return { success: true };
    } catch (err) {
      console.error('remove student_document error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  // convenience
  const refetch = useCallback(async (studentId) => list(studentId), [list]);

  return {
    items,
    loading,
    error,
    list,
    upload,
    getSignedUrl,
    remove,
    refetch,
  };
};
